// RecordingSessionContext.jsx
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useDealershipContext } from "./dealershipContext";
import { v4 as uuidv4 } from "uuid";

// Import the audio worker
import {
  startAudioRecorderWorklet,
  stopMicrophone,
} from "../utils/audio-streaming/audio-recorder";

// Import Audio Streaming Websocket Client
import { audioStreamingClientWS } from "../services/mae-audio-streaming-ws";

// loggin error to new relic
import { newRelicErrorLog } from "../utils/newRelicLogs";

const RecordingSessionContext = createContext({});

const RecordingSessionProvider = ({ children }) => {
  const { dealershipId, baseURL } = useDealershipContext();

  const [recordingEnabled, setRecordingEnabled] = useState(
    () => new URLSearchParams(window.location.search).get("audio") || "0"
  );

  const audioStreamingActive = useRef(null);
  const recorderActive = useRef(null);
  const audioBuffer = useRef([]);
  const bufferTimer = useRef(null);
  const onWebSocketOpenQueue = useRef(Promise.resolve());

  const audioRecorderNode = useRef(null);
  const audioRecorderContext = useRef(null);
  const micStream = useRef(null);
  const source = useRef(null);

  const [audioStatus, setAudioStatus] = useState(false);

  const connectToWebsocketClient = () => {
    localStorage.clear();
    audioStreamingClientWS.connectWebsocket(
      dealershipId,
      uuidv4(),
      onWebsocketOpen,
      onWebsocketMessage,
      onWebsocketClosed,
      onWebsocketError
    );
  };

  const reconnectToWebsocketClient = () => {
    audioStreamingClientWS.reconnectWebsocket(
      dealershipId,
      onWebsocketOpen,
      onWebsocketMessage,
      onWebsocketClosed,
      onWebsocketError
    );
  };

  useEffect(() => {
    if (audioStreamingActive.current) return;
    audioStreamingActive.current = true;
    if (
      recordingEnabled === "2" ||
      recordingEnabled === "1" ||
      recordingEnabled === "3"
    )
      reconnectToWebsocketClient();
  }, []);

  const onWebsocketOpen = async function () {
    // log when the websocket is connected
    console.log("WebSocket connection opened.");
    onWebSocketOpenQueue.current = onWebSocketOpenQueue.current
      .then(async () => {
        if (!recorderActive.current) {
          recorderActive.current = true;
          await stopAudioRecording();
          await startAudioRecording();
        }
      })
      .catch((error) => {
        console.error("Error in recording restart:", error);
        newRelicErrorLog(
          error,
          {
            dealershipId,
            recordingSessionId: "blank:recordingSessionId",
            originated: "RecordingSessionContext",
          },
          "Error in onWebsockeOpen method"
        );
      });
  };

  const onWebsocketClosed = function (event) {
    if (recorderActive.current) {
      recorderActive.current = false;
      setAudioStatus("not-active");
    }

    const recordingSessionId =
      localStorage.getItem("recordingSessionId") || null;
    audioStreamingClientWS.clearInstance(recordingSessionId);
    // log when the websocket is closed
    console.log("WebSocket connection closed.", event);
    newRelicErrorLog(
      {},
      {
        dealershipId,
        recordingSessionId: recordingSessionId || "blank:recordingSessionId",
        originated: "RecordingSessionContext",
      },
      `websocket closed: reason - ${event.reason}, code - ${event.code}`
    );

    const reason = event.reason;

    switch (reason) {
      case "Session Expired":
        connectToWebsocketClient();
        return;

      case "Failed":
        reconnectToWebsocketClient();
        return;

      case "Dropped":
        reconnectToWebsocketClient();
        return;

      case "Completed":
        connectToWebsocketClient();
        return;

      case "keepalive ping timeout":
        reconnectToWebsocketClient();
        return;

      case "Permission Denied":
        return;

      default:
        reconnectToWebsocketClient();
        return;
    }
  };

  const onWebsocketMessage = function (e) {
    const recordingSessionId = localStorage.getItem("recordingSessionId");
    try {
      const parsed = JSON.parse(e.data);
      if (parsed?.type === "ping") {
        audioStreamingClientWS.sendMessageFromInstance(
          recordingSessionId,
          {
            type: "pong",
          },
          dealershipId
        );
      }
    } catch (error) {
      newRelicErrorLog(error, {
        dealershipId,
        recordingSessionId: recordingSessionId || "blank:recordingSessionId",
        originated: "RecordingSessionContext",
      });
    }
  };

  const onWebsocketError = function (e) {
    const recordingSessionId = localStorage.getItem("recordingSessionId");
    // log at the websocket error
    newRelicErrorLog(e, {
      dealershipId,
      recordingSessionId: recordingSessionId || "blank:recordingSessionId",
      originated: "RecordingSessionContext",
    });
    console.log("WebSocket error: ", e);
  };

  async function startAudioRecording() {
    const recordingSessionId = localStorage.getItem("recordingSessionId");
    try {
      const [node, ctx, stream, sourceNode] = await startAudioRecorderWorklet(
        audioRecorderHandler,
        dealershipId,
        recordingSessionId
      );
      if (!node || !ctx || !stream || !sourceNode) {
        audioStreamingClientWS.destroyInstance(
          recordingSessionId,
          "Permission Denied"
        );
      }
      audioRecorderNode.current = node;
      audioRecorderContext.current = ctx;
      micStream.current = stream;
      source.current = sourceNode;
    } catch (error) {
      newRelicErrorLog(error, {
        dealershipId,
        recordingSessionId: recordingSessionId || "blank:recordingSessionId",
        originated: "RecordingSessionContext",
      });
    }
  }

  function audioRecorderHandler(pcmData, currentAudioStatus) {
    const recordingSessionId = localStorage.getItem("recordingSessionId");
    try {
      if (recorderActive.current === true && currentAudioStatus) {
        setAudioStatus(currentAudioStatus);
      }
      audioBuffer.current.push(new Uint8Array(pcmData));
      if (!bufferTimer.current) {
        bufferTimer.current = setInterval(sendBufferedAudio, 1000);
      }
    } catch (error) {
      newRelicErrorLog(error, {
        dealershipId,
        recordingSessionId: recordingSessionId || "blank:recordingSessionId",
        originated: "RecordingSessionContext",
      });
    }
  }

  function sendBufferedAudio() {
    const recordingSessionId = localStorage.getItem("recordingSessionId");
    try {
      if (audioBuffer.current.length === 0) {
        return;
      }

      let totalLength = 0;
      for (const chunk of audioBuffer.current) {
        totalLength += chunk.length;
      }

      const combinedBuffer = new Uint8Array(totalLength);
      let offset = 0;
      for (const chunk of audioBuffer.current) {
        combinedBuffer.set(chunk, offset);
        offset += chunk.length;
      }

      audioStreamingClientWS.sendMessageFromInstance(
        recordingSessionId,
        {
          mime_type: "audio/pcm",
          data: arrayBufferToBase64(combinedBuffer.buffer),
        },
        dealershipId
      );
    } catch (error) {
      newRelicErrorLog(error, {
        dealershipId,
        recordingSessionId: recordingSessionId || "blank:recordingSessionId",
        originated: "RecordingSessionContext",
      });
    } finally {
      audioBuffer.current = [];
    }
  }

  async function stopAudioRecording() {
    const recordingSessionId = localStorage.getItem("recordingSessionId");
    try {
      console.log("stopping microphone");
      if (bufferTimer.current) {
        clearInterval(bufferTimer.current);
        bufferTimer.current = null;
      }
      await stopMicrophone(
        micStream.current,
        audioRecorderNode.current,
        audioRecorderContext.current,
        source.current
      );
    } catch (error) {
      newRelicErrorLog(error, {
        dealershipId,
        recordingSessionId: recordingSessionId || "blank:recordingSessionId",
        originated: "RecordingSessionContext",
      });
    } finally {
      audioBuffer.current = [];
      audioRecorderNode.current = null;
      audioRecorderContext.current = null;
      micStream.current = null;
      source.current = null;
      console.log("recording-cleanup done", recordingSessionId);
    }
  }

  function arrayBufferToBase64(buffer) {
    const recordingSessionId = localStorage.getItem("recordingSessionId");
    try {
      let binary = "";
      const bytes = new Uint8Array(buffer);
      const len = bytes.byteLength;
      for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      return window.btoa(binary);
    } catch (error) {
      newRelicErrorLog(error, {
        dealershipId,
        recordingSessionId: recordingSessionId || "blank:recordingSessionId",
        originated: "RecordingSessionContext",
      });
    }
  }

  return (
    <RecordingSessionContext.Provider
      value={{ recordingEnabled, recorderActive, audioStatus }}
    >
      {children}
    </RecordingSessionContext.Provider>
  );
};

const useRecordingSessionContext = () => {
  return useContext(RecordingSessionContext);
};

export { RecordingSessionProvider, useRecordingSessionContext };
